'use client';

// ============================================================================
// PRODUCT BACKLOG VIEW
// Comprehensive backlog management with sprint planning, prioritization,
// epic grouping, and story point estimation - comparable to Jira/Azure DevOps
// Date: November 9, 2025
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  List,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Star,
  Flag,
  Clock,
  Target,
  Layers,
  Plus,
  Filter,
  Search,
  ArrowUpDown,
  Users,
  Calendar,
  Zap,
  AlertCircle
} from 'lucide-react';
import { authService } from '@/lib/authService';
import WorkItemModal from '@/components/WorkItemModal';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('ProductBacklogView');

interface ProductBacklogViewProps {
  items?: WorkItem[]; // Optional: if provided, use these instead of loading
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
}

interface Epic {
  id: string;
  item_number: string;
  title: string;
  status: string;
  priority: string;
  story_points: number;
  completed_points: number;
  items: WorkItem[];
  collapsed: boolean;
}

interface Sprint {
  id: string;
  title: string;
  status: string;
  start_date: string;
  end_date: string;
}

type ViewMode = 'list' | 'grouped';
type GroupBy = 'epic' | 'priority' | 'none';
type SortBy = 'priority' | 'story_points' | 'created' | 'updated';

export default function ProductBacklogView({ permissions, items: externalItems }: ProductBacklogViewProps) {
  // State
  const [items, setItems] = useState<WorkItem[]>(externalItems || []);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(!externalItems); // Only load if no external items provided
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [groupBy, setGroupBy] = useState<GroupBy>('priority');
  const [sortBy, setSortBy] = useState<SortBy>('priority');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WorkItem | null>(null);
  const [draggedItem, setDraggedItem] = useState<WorkItem | null>(null);
  const [showSprintPlanningModal, setShowSprintPlanningModal] = useState(false);
  const [selectedItemsForSprint, setSelectedItemsForSprint] = useState<string[]>([]);

  // Update items when external items change
  useEffect(() => {
    if (externalItems) {
      setItems(externalItems);
      setLoading(false);
    } else {
      loadBacklogData();
    }
  }, [externalItems]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadBacklogData() {
    // Only load if no external items provided
    if (externalItems) return;
    
    try {
      setLoading(true);
      const token = authService.getTokens()?.access_token;
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // Load backlog items (not in any sprint, not completed/closed)
      const itemsResponse = await fetch(
        '/api/v1/work-items?module_context=software-development&status_not=completed,closed,archived&not_in_sprint=true',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!itemsResponse.ok) {
        throw new Error('Failed to load backlog items');
      }

      const itemsData = await itemsResponse.json();
      const backlogItems = itemsData.items || itemsData.data || [];

      // Load epics
      const epicsResponse = await fetch(
        '/api/v1/work-items?item_type=epic&module_context=software-development',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let epicsList: WorkItem[] = [];
      if (epicsResponse.ok) {
        const epicsData = await epicsResponse.json();
        epicsList = epicsData.items || epicsData.data || [];
      }

      // Load available sprints for planning
      const sprintsResponse = await fetch(
        '/api/v1/work-items?item_type=sprint&module_context=software-development&status_not=completed',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let sprintsList: Sprint[] = [];
      if (sprintsResponse.ok) {
        const sprintsData = await sprintsResponse.json();
        const sprintItems = sprintsData.items || sprintsData.data || [];
        sprintsList = sprintItems.map((s: WorkItem) => ({
          id: s.id,
          title: s.title,
          status: s.status,
          start_date: s.start_date || '',
          end_date: s.due_date || ''
        }));
      }

      setItems(backlogItems);
      setSprints(sprintsList);
      groupItemsByEpic(backlogItems, epicsList);

    } catch (error) {
      logger.error('Failed to load backlog data:', error);
    } finally {
      setLoading(false);
    }
  }

  function groupItemsByEpic(items: WorkItem[], epicsList: WorkItem[]) {
    const epicsMap = new Map<string, Epic>();

    // Initialize epics
    epicsList.forEach(epic => {
      const storyPoints = epic.custom_fields?.story_points || 0;
      epicsMap.set(epic.id, {
        id: epic.id,
        item_number: epic.item_number || epic.id,
        title: epic.title || 'Untitled epic',
        status: epic.status || 'open',
        priority: epic.priority || 'medium',
        story_points: storyPoints,
        completed_points: 0,
        items: [],
        collapsed: false
      });
    });

    // Group items under epics (based on parent relationship)
    // For now, we'll use a simple heuristic: items without epic get "No Epic" group
    const noEpicGroup: Epic = {
      id: 'no-epic',
      item_number: '',
      title: 'No Epic',
      status: 'open',
      priority: 'medium',
      story_points: 0,
      completed_points: 0,
      items: [],
      collapsed: false
    };

    items.forEach(item => {
      // TODO: Load relationships to find parent epic
      // For now, all items go to "No Epic"
      const points = item.custom_fields?.story_points || 0;
      noEpicGroup.story_points += points;
      noEpicGroup.items.push(item);
    });

    const finalEpics = Array.from(epicsMap.values());
    if (noEpicGroup.items.length > 0) {
      finalEpics.push(noEpicGroup);
    }

    setEpics(finalEpics);
  }

  const filteredItems = useMemo(() => {
    let filtered = items;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        (item.title || '').toLowerCase().includes(query) ||
        (item.item_number || '').toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }

    // Priority filter
    if (selectedPriorities.length > 0) {
      filtered = filtered.filter(item =>
        selectedPriorities.includes(item.priority || 'medium')
      );
    }

    // Type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(item =>
        selectedTypes.includes(item.item_type)
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          return priorityOrder[a.priority as keyof typeof priorityOrder] - 
                 priorityOrder[b.priority as keyof typeof priorityOrder];
        case 'story_points':
          return (b.custom_fields?.story_points || 0) - (a.custom_fields?.story_points || 0);
        case 'created':
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case 'updated':
          return new Date(b.updated_at || b.created_at || 0).getTime() - 
                 new Date(a.updated_at || a.created_at || 0).getTime();
        default:
          return 0;
      }
    });
  }, [items, searchQuery, selectedPriorities, selectedTypes, sortBy]);

  const toggleEpicCollapse = (epicId: string) => {
    setEpics(prev => prev.map(epic =>
      epic.id === epicId ? { ...epic, collapsed: !epic.collapsed } : epic
    ));
  };

  const handleItemClick = (item: WorkItem) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const handleDragStart = (item: WorkItem) => {
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItemsForSprint(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const getPriorityIcon = (priority: string) => {
    const icons = {
      critical: <AlertCircle className="w-4 h-4 text-red-600" />,
      high: <Flag className="w-4 h-4 text-orange-500" />,
      medium: <Flag className="w-4 h-4 text-yellow-500" />,
      low: <Flag className="w-4 h-4 text-gray-400" />
    };
    return icons[priority as keyof typeof icons] || icons.medium;
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      epic: <Layers className="w-4 h-4 text-purple-600" />,
      story: <Users className="w-4 h-4 text-blue-600" />,
      task: <Target className="w-4 h-4 text-green-600" />,
      bug: <AlertCircle className="w-4 h-4 text-red-600" />,
      feature: <Zap className="w-4 h-4 text-yellow-600" />
    };
    return icons[type as keyof typeof icons] || <Target className="w-4 h-4 text-gray-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading backlog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewMode === 'grouped'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                Grouped
              </button>
            </div>

            {/* Group By (when grouped mode) */}
            {viewMode === 'grouped' && (
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="epic">Group by Epic</option>
                <option value="priority">Group by Priority</option>
                <option value="none">No Grouping</option>
              </select>
            )}

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm flex items-center gap-2"
            >
              <option value="priority">Sort by Priority</option>
              <option value="story_points">Sort by Story Points</option>
              <option value="created">Sort by Created Date</option>
              <option value="updated">Sort by Updated Date</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {selectedItemsForSprint.length > 0 && (
              <button
                onClick={() => setShowSprintPlanningModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Calendar className="w-4 h-4" />
                Add to Sprint ({selectedItemsForSprint.length})
              </button>
            )}
            {/* New Item button removed — caller provides create actions */}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search backlog items..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-2">
            <button className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-4 text-sm text-gray-600 dark:text-gray-400">
          <span>{items.length} items in backlog</span>
          <span>•</span>
          <span>{items.reduce((sum, item) => sum + (item.custom_fields?.story_points || 0), 0)} total story points</span>
          {selectedItemsForSprint.length > 0 && (
            <>
              <span>•</span>
              <span className="text-purple-600 dark:text-purple-400 font-medium">
                {selectedItemsForSprint.length} selected
              </span>
            </>
          )}
        </div>
      </div>

      {/* Backlog Items */}
      <div className="flex-1 overflow-auto p-6">
        {viewMode === 'grouped' && groupBy === 'epic' ? (
          // Grouped by Epic
          <div className="space-y-4">
            {epics.map(epic => (
              <div key={epic.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Epic Header */}
                <div
                  onClick={() => toggleEpicCollapse(epic.id)}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {epic.collapsed ? (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                    <Layers className="w-5 h-5 text-purple-600" />
                    <div>
                      <div className="flex items-center gap-2">
                        {epic.item_number && (
                          <span className="text-sm font-mono text-gray-500">{epic.item_number}</span>
                        )}
                        <h3 className="font-semibold text-gray-900 dark:text-white">{epic.title}</h3>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{epic.items.length} items</span>
                        <span>•</span>
                        <span>{epic.story_points} story points</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getPriorityIcon(epic.priority)}
                  </div>
                </div>

                {/* Epic Items */}
                {!epic.collapsed && (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {epic.items.map(item => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={() => handleDragStart(item)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
                          selectedItemsForSprint.includes(item.id) ? 'bg-purple-50 dark:bg-purple-900/10' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedItemsForSprint.includes(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                        {getTypeIcon(item.item_type)}
                        <div className="flex-1" onClick={() => handleItemClick(item)}>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-gray-500">{item.item_number}</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{item.title}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {item.custom_fields?.story_points && (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4" />
                              <span>{item.custom_fields.story_points}</span>
                            </div>
                          )}
                          {getPriorityIcon(item.priority || 'medium')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Flat List
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredItems.map(item => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(item)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
                    selectedItemsForSprint.includes(item.id) ? 'bg-purple-50 dark:bg-purple-900/10' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedItemsForSprint.includes(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                  {getTypeIcon(item.item_type)}
                  <div className="flex-1" onClick={() => handleItemClick(item)}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-500">{item.item_number}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{item.title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    {item.custom_fields?.story_points && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        <span>{item.custom_fields.story_points}</span>
                      </div>
                    )}
                    {getPriorityIcon(item.priority || 'medium')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sprint Planning Modal */}
      {showSprintPlanningModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Add to Sprint
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select a sprint to add {selectedItemsForSprint.length} item(s) to:
              </p>
              <div className="space-y-2 mb-6">
                {sprints.map(sprint => (
                  <button
                    key={sprint.id}
                    className="w-full px-4 py-3 text-left border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{sprint.title}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(sprint.start_date).toLocaleDateString()} - {new Date(sprint.end_date).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowSprintPlanningModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <WorkItemModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={async () => {
            setShowCreateModal(false);
            loadBacklogData();
          }}
          itemType="story"
          templates={[]}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <WorkItemDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedItem(null);
          }}
          workItem={selectedItem}
          onUpdate={() => loadBacklogData()}
        />
      )}
    </div>
  );
}
